import { Fragment } from "react";

// An exercise's prompt, line by line. Items carry their layout as plain newlines: a
// passage, then the question; or a lead-in, numbered steps ("1. ", "(a) ", "- "), then
// the question. The browser would run all of it into one paragraph, so each line gets
// its own block and steps hang under their marker. Only spans: the card's prompt is an
// <h2> and a chat widget's is a <p>, which allow phrasing content only. The marker stays
// in the text, so the prompt still reads (and is announced) exactly as written.

export type PromptLine =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "step"; readonly marker: string; readonly text: string };

/** "1." "2)" "(a)" "b)" "-" "•" "–", then a space, at the start of a line. */
const STEP = /^(\d{1,2}[.)]|\([a-z0-9]\)|[a-z]\)|[-•–])\s+(\S.*)$/u;

export function promptLines(prompt: string): PromptLine[] {
  const lines: PromptLine[] = [];
  for (const raw of prompt.split(/\r?\n/u)) {
    const line = raw.trim();
    if (line === "") continue;
    const step = STEP.exec(line);
    const marker = step?.[1];
    const text = step?.[2];
    lines.push(
      marker !== undefined && text !== undefined
        ? { kind: "step", marker, text }
        : { kind: "text", text: line },
    );
  }
  return lines;
}

export type PromptSize = "short" | "long" | "longer";

/**
 * How much room the prompt needs, so the card can shrink its type and keep the answer
 * on screen. Most prompts are under 100 characters and keep the large display size.
 */
export function promptSize(prompt: string): PromptSize {
  const length = prompt.trim().length;
  const lines = promptLines(prompt).length;
  if (length > 220 || lines > 5) return "longer";
  if (length > 110 || lines > 2) return "long";
  return "short";
}

export function PromptText({ text }: { text: string }) {
  const lines = promptLines(text);
  if (lines.length <= 1) return <>{text.trim()}</>;
  return (
    <>
      {lines.map((line, index) => (
        // A newline between blocks keeps the text content readable as one string.
        <Fragment key={index}>
          {index > 0 && "\n"}
          {line.kind === "step" ? (
            <span className="prompt-step">
              <span className="prompt-step-marker">{line.marker}</span> {line.text}
            </span>
          ) : (
            <span className="prompt-line">{line.text}</span>
          )}
        </Fragment>
      ))}
    </>
  );
}
